// Client-side mock data generator for HQ Signal smart office analytics.
// Deterministic via seeded RNG for stable visuals across re-renders.

const DEPARTMENTS = [
  "Engineering",
  "Product",
  "Design",
  "Marketing",
  "Sales",
  "Finance",
  "People Ops",
  "Operations",
];

const FLOORS = [1, 2, 3];
const WORKPLACES_PER_FLOOR = 100;
const HISTORY_DAYS = 60;

// Monthly seasonality factor (1.0 = baseline). Captures summer dip,
// winter holidays, and the post-holiday ramp-up.
const MONTHLY_FACTOR = {
  0: 0.82, // Jan — back from holidays
  1: 0.92, // Feb
  2: 0.97, // Mar
  3: 1.0, // Apr
  4: 1.06, // May — peak office activity
  5: 1.02, // Jun
  6: 0.85, // Jul — summer dip starts
  7: 0.72, // Aug — peak holiday season
  8: 1.04, // Sep — la rentrée
  9: 1.07, // Oct — peak again
  10: 1.02, // Nov
  11: 0.78, // Dec — winter holidays
};

// Meeting rooms — 2 per floor.
const MEETING_ROOMS = [
  { id: "alpha", name: "Alpha", floor: 1, capacity: 10 },
  { id: "beta", name: "Beta", floor: 1, capacity: 6 },
  { id: "gamma", name: "Gamma", floor: 2, capacity: 12 },
  { id: "delta", name: "Delta", floor: 2, capacity: 4 },
  { id: "epsilon", name: "Epsilon", floor: 3, capacity: 8 },
  { id: "zeta", name: "Zeta", floor: 3, capacity: 16 },
];

// Mulberry32 seeded RNG for stable values.
function mulberry32(seed) {
  return function () {
    let t = (seed += 0x6d2b79f5);
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

const rand = mulberry32(42);

function dayPattern(date) {
  // Monday=1 ... Friday=5 ... Sun=0/Sat=6
  const d = date.getDay();
  if (d === 0 || d === 6) return 0.05; // weekend skeleton crew
  if (d === 5) return 0.55; // Friday lower
  if (d === 1) return 0.78; // Monday
  if (d === 2 || d === 3) return 1.0; // Tue/Wed peak
  return 0.92; // Thursday
}

// Build workplaces (300 total, balanced across floors and departments).
function buildWorkplaces() {
  const result = [];
  let id = 1;
  for (const floor of FLOORS) {
    for (let i = 0; i < WORKPLACES_PER_FLOOR; i++) {
      const dept = DEPARTMENTS[i % DEPARTMENTS.length];
      result.push({
        id: `WP-${String(id).padStart(3, "0")}`,
        floor,
        department: dept,
        zone: `Z${Math.floor(i / 10) + 1}`,
      });
      id += 1;
    }
  }
  return result;
}

// Build 60 days of history with weekday + monthly seasonality + a slow
// sinusoidal modulation across the window.
function buildHistory() {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const days = [];
  for (let i = HISTORY_DAYS - 1; i >= 0; i--) {
    const d = new Date(today);
    d.setDate(today.getDate() - i);
    const base = dayPattern(d);
    const seasonal = MONTHLY_FACTOR[d.getMonth()] ?? 1.0;
    // Bi-weekly cycle on top of weekday + monthly factors.
    const dayIndex = HISTORY_DAYS - 1 - i;
    const wave = 1.0 + 0.07 * Math.sin((dayIndex / HISTORY_DAYS) * 2 * Math.PI);
    const noise = (rand() - 0.5) * 0.06;
    const occupancy = Math.max(
      0.04,
      Math.min(0.95, base * seasonal * wave * 0.65 + noise + 0.05),
    );
    days.push({
      date: d.toISOString().slice(0, 10),
      iso: d.toISOString(),
      weekday: d.toLocaleDateString("en-US", { weekday: "short" }),
      month: d.toLocaleDateString("en-US", { month: "short" }),
      occupancyRate: Number(occupancy.toFixed(3)),
      seasonalFactor: Number(seasonal.toFixed(3)),
    });
  }
  return days;
}

// Average occupancy by weekday across the window.
function buildWeekdayDistribution(history) {
  const order = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
  const buckets = {};
  history.forEach((d) => {
    if (!buckets[d.weekday]) buckets[d.weekday] = { sum: 0, count: 0 };
    buckets[d.weekday].sum += d.occupancyRate;
    buckets[d.weekday].count += 1;
  });
  return order
    .filter((w) => buckets[w])
    .map((w) => ({
      weekday: w,
      occupancyRate: Number((buckets[w].sum / buckets[w].count).toFixed(3)),
    }));
}

// Average occupancy per calendar month present in the window.
function buildMonthlyDistribution(history) {
  const buckets = {};
  history.forEach((d) => {
    if (!buckets[d.month]) buckets[d.month] = { sum: 0, count: 0 };
    buckets[d.month].sum += d.occupancyRate;
    buckets[d.month].count += 1;
  });
  return Object.entries(buckets).map(([month, b]) => ({
    month,
    occupancyRate: Number((b.sum / b.count).toFixed(3)),
  }));
}

// Per-department average occupancy across the 60-day window.
function buildDepartmentStats(history) {
  const meanOcc =
    history.reduce((s, d) => s + d.occupancyRate, 0) / history.length;
  return DEPARTMENTS.map((dept, idx) => {
    // Each department offsets the mean differently to show variance.
    const offsets = [0.12, 0.04, -0.02, 0.08, -0.05, -0.12, -0.08, 0.0];
    const off = offsets[idx % offsets.length];
    const occ = Math.max(0.18, Math.min(0.82, meanOcc + off));
    const headcount = 300 / DEPARTMENTS.length; // 37.5 nominal
    const allocatedDesks = Math.round(headcount);
    const usedDesks = Math.round(allocatedDesks * occ);
    return {
      department: dept,
      occupancyRate: Number(occ.toFixed(3)),
      allocatedDesks,
      usedDesks,
      unusedDesks: allocatedDesks - usedDesks,
    };
  });
}

function buildFloorStats(history) {
  const meanOcc =
    history.reduce((s, d) => s + d.occupancyRate, 0) / history.length;
  const offsets = { 1: 0.06, 2: -0.02, 3: -0.09 };
  return FLOORS.map((floor) => {
    const occ = Math.max(0.2, Math.min(0.85, meanOcc + offsets[floor]));
    const used = Math.round(WORKPLACES_PER_FLOOR * occ);
    return {
      floor,
      occupancyRate: Number(occ.toFixed(3)),
      totalWorkplaces: WORKPLACES_PER_FLOOR,
      usedWorkplaces: used,
      unusedWorkplaces: WORKPLACES_PER_FLOOR - used,
    };
  });
}

function buildMeetingRoomStats() {
  // Each room: avgOccupancy (people), bookingRate (% time booked),
  // noShowRate (% bookings without presence).
  const roomConfigs = [
    { id: "alpha", avgUsage: 3, booking: 0.78, noShow: 0.32 },
    { id: "beta", avgUsage: 2, booking: 0.62, noShow: 0.18 },
    { id: "gamma", avgUsage: 4, booking: 0.81, noShow: 0.27 },
    { id: "delta", avgUsage: 2, booking: 0.71, noShow: 0.22 },
    { id: "epsilon", avgUsage: 3, booking: 0.55, noShow: 0.16 },
    { id: "zeta", avgUsage: 5, booking: 0.84, noShow: 0.36 },
  ];
  return MEETING_ROOMS.map((room) => {
    const cfg = roomConfigs.find((r) => r.id === room.id);
    const efficiency = cfg.avgUsage / room.capacity;
    return {
      ...room,
      avgUsage: cfg.avgUsage,
      bookingRate: cfg.booking,
      noShowRate: cfg.noShow,
      efficiency: Number(efficiency.toFixed(3)),
      underused: efficiency < 0.5,
    };
  });
}

// Per-workplace utilization rollup.
function buildWorkplaceUtilization(workplaces, history) {
  const meanOcc =
    history.reduce((s, d) => s + d.occupancyRate, 0) / history.length;
  return workplaces.map((wp, idx) => {
    // Stagger occupancy by a deterministic offset per workplace id.
    const offset = ((idx * 17) % 100) / 100 - 0.5; // -0.5 .. +0.5
    const occ = Math.max(0.05, Math.min(0.95, meanOcc + offset * 0.5));
    return {
      ...wp,
      occupancyRate: Number(occ.toFixed(3)),
      activeDays: Math.round(occ * HISTORY_DAYS),
    };
  });
}

function buildKPIs(history, departmentStats, meetingRoomStats) {
  const deskOccupancy =
    history.reduce((s, d) => s + d.occupancyRate, 0) / history.length;
  const meetingEfficiency =
    meetingRoomStats.reduce((s, r) => s + r.efficiency, 0) /
    meetingRoomStats.length;
  const noShow =
    meetingRoomStats.reduce((s, r) => s + r.noShowRate, 0) /
    meetingRoomStats.length;
  // Potential optimization = portion of capacity that is consistently unused.
  const unusedShare = 1 - deskOccupancy;
  const optimization = Math.max(0, unusedShare - 0.2); // headroom buffer 20%
  return {
    deskOccupancyRate: Number(deskOccupancy.toFixed(3)),
    meetingEfficiency: Number(meetingEfficiency.toFixed(3)),
    noShowRate: Number(noShow.toFixed(3)),
    optimizationPotential: Number(optimization.toFixed(3)),
    deskOccupancyDelta: 0.024,
    meetingEfficiencyDelta: -0.011,
    noShowDelta: -0.018,
    optimizationDelta: 0.032,
  };
}

function buildInsights(kpis, departmentStats, meetingRoomStats) {
  const unusedDesksPct = 1 - kpis.deskOccupancyRate;
  const wastedRoomCapacity = 1 - kpis.meetingEfficiency;
  const reduceDesksPct = Math.round(kpis.optimizationPotential * 100);
  // Estimate savings: assume desk cost €6,000 / year fully loaded.
  const annualDeskCost = 6000;
  const totalDesks = 300;
  const desksToReduce = Math.round((reduceDesksPct / 100) * totalDesks);
  const estimatedSavings = desksToReduce * annualDeskCost;

  // Highest and lowest dept by occupancy.
  const sortedDept = [...departmentStats].sort(
    (a, b) => b.occupancyRate - a.occupancyRate,
  );
  const peakDept = sortedDept[0];
  const lowDept = sortedDept[sortedDept.length - 1];

  const underusedRooms = meetingRoomStats.filter((r) => r.underused);

  return {
    unusedDesksPct: Number(unusedDesksPct.toFixed(3)),
    wastedRoomCapacity: Number(wastedRoomCapacity.toFixed(3)),
    reduceDesksPct,
    desksToReduce,
    estimatedSavings,
    peakDept,
    lowDept,
    underusedRooms,
  };
}

export function getMockData() {
  const workplaces = buildWorkplaces();
  const history = buildHistory();
  const departmentStats = buildDepartmentStats(history);
  const floorStats = buildFloorStats(history);
  const meetingRoomStats = buildMeetingRoomStats();
  const workplaceUtilization = buildWorkplaceUtilization(workplaces, history);
  const kpis = buildKPIs(history, departmentStats, meetingRoomStats);
  const insights = buildInsights(kpis, departmentStats, meetingRoomStats);
  const weekdayDistribution = buildWeekdayDistribution(history);
  const monthlyDistribution = buildMonthlyDistribution(history);

  // Lightweight 30-point sparklines for KPI cards.
  const baseSeries = history.slice(-30).map((d) => d.occupancyRate);
  const sparklines = {
    deskOccupancy: baseSeries,
    meetingEfficiency: baseSeries.map((v) =>
      Number((Math.max(0.1, v * 0.85 + 0.02)).toFixed(3)),
    ),
    noShow: baseSeries.map((v) =>
      Number(Math.max(0.05, 0.42 - v * 0.32).toFixed(3)),
    ),
    optimization: baseSeries.map((v) =>
      Number(Math.max(0.05, 0.55 - v * 0.4).toFixed(3)),
    ),
  };

  // "Today" snapshot (most recent day in history).
  const todaySnapshot = history[history.length - 1];
  const bestFloor = [...floorStats].sort(
    (a, b) => b.occupancyRate - a.occupancyRate,
  )[0];
  const topDepartment = [...departmentStats].sort(
    (a, b) => b.occupancyRate - a.occupancyRate,
  )[0];

  return {
    departments: DEPARTMENTS,
    floors: FLOORS,
    workplaces,
    history,
    departmentStats,
    floorStats,
    meetingRoomStats,
    workplaceUtilization,
    kpis,
    insights,
    weekdayDistribution,
    monthlyDistribution,
    sparklines,
    todaySnapshot,
    bestFloor,
    topDepartment,
  };
}

export const META = {
  DEPARTMENTS,
  FLOORS,
  WORKPLACES_PER_FLOOR,
  HISTORY_DAYS,
};
